import { Wrench, CheckCircle, ArrowRight, Phone, Armchair, CookingPot, BedDouble, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { CTABanner } from '../components/CTABanner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://panthera-umzug.de';
const typeIcons = [CookingPot, Armchair, BedDouble, BookOpen];

export default function MobelmontageBerlinPage() {
  const { t } = useLanguage();
  const m = t.montage;

  return (
    <>
      <SEOHead title="Möbelmontage Berlin | Fachgerechter Aufbau" description="Professionelle Möbelmontage in Berlin: IKEA Montage, Küchenmontage, Möbelaufbau vom Profi." canonical={`${BASE_URL}/moebelmontage-berlin`} />

      <section className="relative pt-32 pb-20 bg-black overflow-hidden" data-testid="montage-hero">
        <div className="absolute inset-0"><div className="absolute bottom-10 left-20 w-80 h-80 bg-[#D4AF37] rounded-full filter blur-[140px] opacity-15" /></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full">
              <Wrench className="w-4 h-4 text-[#D4AF37]" /><span className="text-[#D4AF37] text-sm font-medium">{m.badge}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="montage-h1">
              <span className="text-white">{m.h1_1}</span>
              <span className="bg-gradient-to-r from-[#F1D27A] via-[#D4AF37] to-[#8F6B1F] bg-clip-text text-transparent">{m.h1_2}</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-3xl mb-8">{m.subtitle}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/kontakt"><Button size="lg" className="bg-gradient-to-r from-[#F1D27A] via-[#D4AF37] to-[#8F6B1F] text-black font-bold px-8 py-6 rounded-lg hover:scale-105 transition-transform shadow-lg shadow-[#D4AF37]/30">{t.header.cta} <ArrowRight className="ml-2 w-5 h-5" /></Button></Link>
              <a href="tel:+491631945793"><Button size="lg" variant="outline" className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-bold px-8 py-6 rounded-lg hover:scale-105 transition-all"><Phone className="mr-2 w-5 h-5" /> {t.hero.cta2}</Button></a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-4 text-gray-300 leading-relaxed">
            <p>{m.intro_p1}</p><p>{m.intro_p2}</p><p>{m.intro_p3}</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-black" data-testid="montage-types">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">{m.types_title}</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {m.types.map((item, i) => {
              const Icon = typeIcons[i];
              return (
                <Card key={i} className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-[#2a2a2a] hover:border-[#D4AF37]/40 transition-all duration-300" data-testid={`montage-type-${i}`}>
                  <CardHeader>
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#8F6B1F]/20 flex items-center justify-center mb-3"><Icon className="w-7 h-7 text-[#D4AF37]" /></div>
                    <CardTitle className="text-xl text-white">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent><p className="text-gray-400 leading-relaxed">{item.desc}</p></CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">{m.includes_title}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {m.includes.map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-[#111] rounded-lg border border-[#2a2a2a]">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" /><span className="text-gray-300">{item}</span>
                </div>
              ))}
            </div>
            <div className="mt-10 text-gray-300 leading-relaxed"><p>{m.outro}</p></div>
          </div>
        </div>
      </section>

      <CTABanner titleKey={m.cta_title} subtitleKey={m.cta_sub} />
    </>
  );
}
